import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiX, FiSave, FiEdit3 } from 'react-icons/fi';

function slugifyId(input) {
  return String(input || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

const WhatsAppTemplateEditor = ({ template, onClose, onSaved }) => {
  const isEdit = Boolean(template && template.id);

  const initial = useMemo(() => {
    const t = template || {};
    return {
      id: t.id || '',
      name: t.name || '',
      category: t.category || 'starter',
      templateMode: t.templateMode || 'document',
      metaTemplateName: t.metaTemplateName || 'dietplan_and_literature',
      languageCode: t.languageCode || 'en_US',
      documentLink: t.documentLink || '',
      documentText: t.documentText || '',
    };
  }, [template]);

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(initial);
  }, [initial]);

  const computedId = useMemo(() => {
    if (form.id) return form.id;
    const base = `${slugifyId(form.metaTemplateName)}_${slugifyId(form.languageCode)}`;
    return base ? base : `wa_tpl_${Date.now()}`;
  }, [form.id, form.metaTemplateName, form.languageCode]);

  const handleSave = async () => {
    if (!form.metaTemplateName.trim()) {
      toast.error('Meta template name is required');
      return;
    }
    if (!form.languageCode.trim()) {
      toast.error('Language code is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        id: computedId,
      };
      await axios.post('/api/whatsapp/templates', payload);
      toast.success('WhatsApp template saved');
      if (onSaved) await onSaved(payload);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <FiEdit3 className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-white font-bold">
              {isEdit ? 'Edit WhatsApp Template' : 'New WhatsApp Template'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-all"
            type="button"
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1">
                Display name
              </label>
              <input
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Diet Plan for customer"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1">
                Category
              </label>
              <input
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                placeholder="starter / marketing / support"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1">
                Template mode
              </label>
              <select
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                value={form.templateMode}
                onChange={(e) => setForm((f) => ({ ...f, templateMode: e.target.value }))}
              >
                <option value="document">Document template (header media)</option>
                <option value="simple">Simple text template</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1">
                Meta template name
              </label>
              <input
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                value={form.metaTemplateName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, metaTemplateName: e.target.value }))
                }
                placeholder="dietplan_and_literature"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1">
                Language code
              </label>
              <input
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                value={form.languageCode}
                onChange={(e) =>
                  setForm((f) => ({ ...f, languageCode: e.target.value }))
                }
                placeholder="en_US"
              />
            </div>
          </div>

          {form.templateMode === 'document' && (
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1">
                Document URL (public HTTPS)
              </label>
              <input
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                value={form.documentLink}
                onChange={(e) =>
                  setForm((f) => ({ ...f, documentLink: e.target.value }))
                }
                placeholder="https://.../document.pdf"
              />
            </div>
          )}

          {form.templateMode === 'simple' && (
            <div className="rounded-lg border border-sky-100 bg-sky-50/90 px-3 py-2 text-xs text-sky-950 space-y-1">
              <p className="font-semibold">Simple template + static URL button (e.g. “Visit website”)</p>
              <p>
                Put the full link only in <strong>Meta</strong> (template editor). Here, leave{' '}
                <strong>Body text parameter</strong> empty if Meta has no <code className="bg-white/80 px-1 rounded">{'{{1}}'}</code>.
                On the main WhatsApp page, leave <strong>URL button suffix</strong> empty so the API does not add a dynamic tail.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1">
              {form.templateMode === 'simple'
                ? 'Body text parameter (only if Meta template has {{1}}, {{2}}, …)'
                : 'Body text parameter'}
            </label>
            <textarea
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm min-h-[120px]"
              value={form.documentText}
              onChange={(e) =>
                setForm((f) => ({ ...f, documentText: e.target.value }))
              }
              placeholder={
                form.templateMode === 'simple'
                  ? 'Leave empty when the body in Meta is fixed text (no variables). Otherwise only the short values for {{1}}, {{2}}, … — not the full template sentence.'
                  : 'Text that goes into the template body variable'
              }
            />
          </div>

          <div className="text-xs text-neutral-500">
            Stored template id: <span className="font-mono">{computedId}</span>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg text-sm font-semibold hover:bg-neutral-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
          >
            <FiSave className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save template'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppTemplateEditor;

